import { ConfigRecord, DesiredStateItem, PermissionRecord, SiteRecord, UserRecord, StateRecord, DesiredStateRecord, TokenRecord } from './config';
import { ScopedContainer, ScopedStorage, ScopedList } from './utils/ScopedStorage';
import { AlertRecord } from './alert';

// Scope is Site's UUID
export class SiteStorage extends ScopedStorage {
    Info:ScopedContainer<SiteRecord>;
    Alerts:ScopedList<AlertRecord>;
    Configs:ScopedList<ConfigRecord<any>>;
    States:ScopedList<StateRecord>;
    DesiredStates:ScopedList<DesiredStateItem>;

    constructor(initialScopes?) {
        super();
        this.addContainer("Info", new ScopedContainer<SiteRecord>(SiteRecord));
        this.addContainer("Alerts", new ScopedList<AlertRecord>(AlertRecord));
        this.addContainer("Configs", new ScopedList<ConfigRecord<any>>(null, null, 'fileName'));
        this.addContainer("States", new ScopedList<StateRecord>(StateRecord));
        this.addContainer("DesiredStates", new ScopedList<DesiredStateItem>(DesiredStateItem, null, 'ControlUUID'));

        if (initialScopes)
            this.init(Array.isArray(initialScopes) ? initialScopes : [ initialScopes ]);
    }
    
    init(initialScopes = []) {
        for (let scopeDesc of initialScopes) {
            for (let contKey of Object.keys(this.database)) {
                if (scopeDesc[contKey])
                    this[contKey]
                        .set(
                            scopeDesc[contKey],
                            scopeDesc.Info.uuid,
                        );
            }
        }
        
        if (this.Info.getSelectedScope()) {
            this.setSelectedScope(this.Info.getSelectedScope());
        }
    }
}

// Scope is User's UUID
export class UserStorage extends ScopedStorage {
    Info:ScopedContainer<UserRecord>;
    Sites:ScopedList<SiteStorage>;
    Tokens:ScopedList<TokenRecord>;
    Permissions:ScopedList<PermissionRecord>;

    constructor(initialScopes?) {
        super();
        this.addContainer("Info", new ScopedContainer<UserRecord>(UserRecord));
        this.addContainer("Sites", new ScopedList<SiteStorage>(SiteStorage, null, 'Info.getKey|uuid'));
        this.addContainer("Permissions", new ScopedList<PermissionRecord>(PermissionRecord));
        this.addContainer("Tokens", new ScopedList<TokenRecord>(TokenRecord, null));

        if (initialScopes)
            this.init(initialScopes);
    }

    init(initialScopes = []) {
        for (let scopeDesc of initialScopes) {
            for (let contKey of Object.keys(this.database)) {
                let val = scopeDesc[contKey];
                if (val) {
                    this[contKey]
                        .set(
                            val,
                            scopeDesc.Info.uuid
                        );
                }
            }
        }
        
        if (this.Info.getSelectedScope()) {
            this.setSelectedScope(this.Info.getSelectedScope());
        }
    }

    // trying not to lose nested info
    setSites(sites:SiteRecord[]) {
        let prevSites = this.Sites.all();
        this.Sites.clear();
        for (let siteRec of sites) {
            let ex = prevSites.find(site => site.Info.getKey('uuid') === siteRec.uuid);
            if (!ex) {
                ex = new SiteStorage();
            }
            ex.Info.set(siteRec);
            this.Sites.push(ex);
        }
    }

    mergeSites(sites:SiteRecord[]) {
        for (let siteRec of sites) {
            let ex = this.Sites.find(siteRec.uuid);
            if (!ex) {
                ex = new SiteStorage();
                this.Sites.push(ex);
            }
            Object.assign(ex.Info, siteRec);
        }
    }

    setPermissions(records:PermissionRecord[]) {
        let itemsBySite = {};
        for (let itemRec of records) {
            if (itemsBySite[itemRec.siteId])
                itemsBySite[itemRec.siteId] = [];
            itemsBySite[itemRec.siteId].push(itemRec);
        }
        for (let siteId of Object.keys(itemsBySite)) {
            let site = this.Sites.find(itemsBySite[siteId].siteId, 'Info.getKey|id');
            if (site)
                site.Permissions.set(itemsBySite[siteId]);
        }
    }

    mergePermissions(perms:PermissionRecord[]) {
        for (let permRec of perms) {
            let site = this.Sites.find(permRec.siteId, 'Info.getKey|id');
            for (let permRec of perms) {
                site.Permissions.set(permRec);
            }
        }
    }

    setTokens(records:TokenRecord[]) {
        let itemsBySite = {};
        for (let itemRec of records) {
            if (itemsBySite[itemRec.siteId])
                itemsBySite[itemRec.siteId] = [];
            itemsBySite[itemRec.siteId].push(itemRec);
        }
        for (let siteId of Object.keys(itemsBySite)) {
            let site = this.Sites.find(itemsBySite[siteId].siteId, 'Info.getKey|id');
            if (site)
                site.Tokens.set(itemsBySite[siteId]);
        }
    }

    mergeTokens(tokens:TokenRecord[]) {
        for (let tokenRec of tokens) {
            this.Tokens.set(tokenRec);
        }
    }

    setConfigs(records:ConfigRecord<any>[]) {
        this.setSiteList('Configs', records);
    }

    mergeConfigs(configs:ConfigRecord<any>[]) {
        this.mergeSiteList('Configs', configs);
    }

    setAlerts(records:AlertRecord[]) {
        this.setSiteList('Alerts', records);
    }

    mergeAlerts(alerts:AlertRecord[]) {
        this.mergeSiteList('Alerts', alerts);
    }

    mergeStates(states:StateRecord[]) {
        this.mergeSiteList('States', states);
    }

    mergeDesiredStates(records:DesiredStateRecord[]) {
        this.mergeSiteList('DesiredStates', records);
    }

    mergeSiteList(listName, records:any[]) {
        for (let configRec of records) {
            let site = this.Sites.find(configRec.siteId, 'Info.getKey|id');
            if (site && site[listName]) {
                site[listName].setItem(configRec);
            }
        }
    }

    setSiteList(listName, records:any[]) {
        let itemsBySite = {};
        for (let itemRec of records) {
            if (itemsBySite[itemRec.siteId])
                itemsBySite[itemRec.siteId] = [];
            itemsBySite[itemRec.siteId].push(itemRec);
        }
        for (let siteId of Object.keys(itemsBySite)) {
            let site = this.Sites.find(itemsBySite[siteId].siteId, 'Info.getKey|id');
            if (site && site[listName])
                site[listName].set(itemsBySite[siteId]);
        }
    }
}

export const User = new UserStorage();

